import { useEffect } from 'react';

const IMAGE_MINIMAP_DISMISS_IGNORE_SELECTOR = [
  '[data-image-minimap]',
  '[data-image-minimap-controls]',
  '[data-image-zoom-surface]',
].join(', ');

export type ImageMinimapDismissReason = 'escape' | 'pointer';

type UseDismissImageMinimapParams = {
  isOpen: boolean;
  onDismiss: (reason: ImageMinimapDismissReason) => void;
};

const ESCAPE_BLOCKING_LAYER_SELECTOR = [
  '[role="tooltip"]',
  '[role="menu"]',
  '[role="dialog"][data-state="open"]',
  '[data-radix-popper-content-wrapper] > [data-state="open"]',
].join(', ');

const isEscapeBlockingLayerOpen = () =>
  Array.from(document.querySelectorAll(ESCAPE_BLOCKING_LAYER_SELECTOR)).some(
    (layer) => !layer.querySelector('[data-image-zoom-surface]'),
  );

export const useDismissImageMinimap = ({ isOpen, onDismiss }: UseDismissImageMinimapParams) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (!(target instanceof Element) || target.closest(IMAGE_MINIMAP_DISMISS_IGNORE_SELECTOR)) {
        return;
      }

      onDismiss('pointer');
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || isEscapeBlockingLayerOpen()) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      onDismiss('escape');
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, onDismiss]);
};
